import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cx } from "@/lib/format";
import { describeError } from "./States";
import { Handle } from "./Chip";
import { IconButton } from "./Button";

/**
 * What just happened, said once, at the corner of the screen.
 *
 * # WHAT A TOAST IS NOT ALLOWED TO BE
 *
 * The only place something is said. It is announced politely rather than
 * assertively, it is never the sole record of a change, and anything a person
 * might need in five minutes — a reference id, a figure, a new address —
 * belongs on the screen as well. A notification is a courtesy.
 *
 * # WHY FAILURES DO NOT EXPIRE
 *
 * A success can go: the screen behind it already shows the new state. A
 * failure usually carries a reference somebody has to copy into a ticket, and
 * six seconds is not long enough to notice it, read it and decide to keep it.
 * So failures stay until dismissed.
 */
type ToastTone = "info" | "success" | "warning" | "danger";

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  reference?: string;
}

interface ToastApi {
  show(toast: Omit<Toast, "id">): void;
  success(title: string, description?: string): void;
  error(error: unknown): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The toast API. Throws outside the provider, which is a wiring mistake
 * rather than a runtime condition worth handling gracefully.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside <ToastProvider>");
  return api;
}

const DISMISS_AFTER_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { ...toast, id }]);
      if (toast.tone !== "danger") {
        timers.current.set(id, window.setTimeout(() => dismiss(id), DISMISS_AFTER_MS));
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description) => show({ tone: "success", title, description }),
      error: (error) => {
        const { title, message, requestId } = describeError(error);
        show({ tone: "danger", title, description: message, reference: requestId });
      },
    }),
    [show],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => window.clearTimeout(timer));
      map.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className={cx(
          "pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col gap-2 p-4",
          "items-center sm:items-end",
        )}
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <Card key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * The lamp each tone shows.
 *
 * A lamp and a word, not a tinted card. Four tinted panels is four blocks of
 * colour where the colour was meant to be the signal — and at the edge of
 * vision, which is where a toast is first noticed, a tint is a flash while a
 * lamp beside a word is a reading.
 */
const LAMPS: Record<ToastTone, string> = {
  info: "var(--primary)",
  success: "var(--success)",
  warning: "var(--warning)",
  danger: "var(--danger)",
};

function Card({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  return (
    <div
      className={cx(
        "overlay-surface pointer-events-auto flex w-full max-w-sm items-start gap-3 p-3",
        "animate-rise",
      )}
    >
      <span
        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
        style={{
          background: LAMPS[toast.tone],
          boxShadow: `0 0 0 3px color-mix(in oklab, ${LAMPS[toast.tone]} 18%, transparent)`,
        }}
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <p className="t-strong">{toast.title}</p>
        {toast.description && (
          <p className="t-meta mt-0.5 leading-4">{toast.description}</p>
        )}
        {toast.reference && (
          <div className="mt-2">
            <Handle value={toast.reference} label="Reference" />
          </div>
        )}
      </div>

      <IconButton label="Dismiss" size="dense" onClick={onDismiss}>
        <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
          <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </IconButton>
    </div>
  );
}
